const $b5b4c813372c82a3$var$msg = document.getElementById('error');
function $b5b4c813372c82a3$export$2e2bcd8739ae039(error) {
    console.error(error);
    $b5b4c813372c82a3$var$msg.classList.remove("hidden");
    $b5b4c813372c82a3$var$msg.innerText = `Error: ${error.message}`;
}
function $b5b4c813372c82a3$export$a15ba4bdf59dacc9() {
    $b5b4c813372c82a3$var$msg.classList.add("hidden");
}


/*!
Copyright (c) 2023 Paul Miller (paulmillr.com)
The library paulmillr-qr is dual-licensed under the Apache 2.0 OR MIT license.
You can select a license of your choice.
Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/ /**
 * Methods for encoding (generating) QR code patterns.
 * Check out decode.ts for decoding (reading).
 * @module
 * @example
```js
import encodeQR from 'qr';
const txt = 'Hello world';
const ascii = encodeQR(txt, 'ascii'); // Not all fonts are supported
const terminalFriendly = encodeQR(txt, 'term'); // 2x larger, all fonts are OK
const gifBytes = encodeQR(txt, 'gif'); // Uncompressed GIF
const svgElement = encodeQR(txt, 'svg'); // SVG vector image element
const array = encodeQR(txt, 'raw'); // 2d array for canvas or other libs
// import decodeQR from 'qr/decode.js';
```
 */ // We do not use newline escape code directly in strings because it's not parser-friendly
const $631ab463e0c75d0e$var$chCodes = {
    newline: 10,
    reset: 27
};
function $631ab463e0c75d0e$var$assertNumber(n) {
    if (!Number.isSafeInteger(n)) throw new Error(`integer expected: ${n}`);
}
function $631ab463e0c75d0e$var$validateVersion(ver) {
    if (!Number.isSafeInteger(ver) || ver < 1 || ver > 40) throw new Error(`Invalid version=${ver}. Expected number [1..40]`);
}
function $631ab463e0c75d0e$var$bin(dec, pad) {
    return dec.toString(2).padStart(pad, '0');
}
function $631ab463e0c75d0e$var$mod(a, b) {
    const result = a % b;
    return result >= 0 ? result : b + result;
}
function $631ab463e0c75d0e$var$fillArr(length, val) {
    return new Array(length).fill(val);
}
/**
 * Interleaves byte blocks.
 * @param blocks [[1, 2, 3], [4, 5, 6]]
 * @returns [1, 4, 2, 5, 3, 6]
 */ function $631ab463e0c75d0e$var$interleaveBytes(...blocks) {
    let len = 0;
    for (const b of blocks)len = Math.max(len, b.length);
    const res = [];
    for(let i = 0; i < len; i++)for (const b of blocks){
        if (i >= b.length) continue; // outside of block, skip
        res.push(b[i]);
    }
    return new Uint8Array(res);
}
function $631ab463e0c75d0e$var$includesAt(lst, pattern, index) {
    if (index < 0 || index + pattern.length > lst.length) return false;
    for(let i = 0; i < pattern.length; i++)if (pattern[i] !== lst[index + i]) return false;
    return true;
}
// Optimize for minimal score/penalty
function $631ab463e0c75d0e$var$best() {
    let best;
    let bestScore = Infinity;
    return {
        add (score, value) {
            if (score >= bestScore) return;
            best = value;
            bestScore = score;
        },
        get: ()=>best,
        score: ()=>bestScore
    };
}
// Based on https://github.com/paulmillr/scure-base/blob/main/index.ts
function $631ab463e0c75d0e$var$alphabet(alphabet) {
    return {
        has: (char)=>alphabet.includes(char),
        decode: (input)=>{
            if (!Array.isArray(input) || input.length && typeof input[0] !== 'string') throw new Error('alphabet.decode input should be array of strings');
            return input.map((letter)=>{
                if (typeof letter !== 'string') throw new Error(`alphabet.decode: not string element=${letter}`);
                const index = alphabet.indexOf(letter);
                if (index === -1) throw new Error(`Unknown letter: "${letter}". Allowed: ${alphabet}`);
                return index;
            });
        },
        encode: (digits)=>{
            if (!Array.isArray(digits) || digits.length && typeof digits[0] !== 'number') throw new Error('alphabet.encode input should be an array of numbers');
            return digits.map((i)=>{
                $631ab463e0c75d0e$var$assertNumber(i);
                if (i < 0 || i >= alphabet.length) throw new Error(`Digit index outside alphabet: ${i} (alphabet: ${alphabet.length})`);
                return alphabet[i];
            });
        }
    };
}
class $631ab463e0c75d0e$export$3924f7882b5e2b61 {
    static size(size, limit) {
        if (typeof size === 'number') size = {
            height: size,
            width: size
        };
        if (!Number.isSafeInteger(size.height) && size.height !== Infinity) throw new Error(`Bitmap: invalid height=${size.height} (${typeof size.height})`);
        if (!Number.isSafeInteger(size.width) && size.width !== Infinity) throw new Error(`Bitmap: invalid width=${size.width} (${typeof size.width})`);
        if (limit !== undefined) // Clamp length, so it won't overflow, also allows to use Infinity, so we draw until end
        size = {
            width: Math.min(size.width, limit.width),
            height: Math.min(size.height, limit.height)
        };
        return size;
    }
    static fromString(s) {
        // Remove linebreaks on start and end, so we draw in `` section
        s = s.replace(/^\n+/g, '').replace(/\n+$/g, '');
        const lines = s.split(String.fromCharCode($631ab463e0c75d0e$var$chCodes.newline));
        const height = lines.length;
        const data = new Array(height);
        let width;
        for (const line of lines){
            const row = line.split('').map((i)=>{
                if (i === 'X') return true;
                if (i === ' ') return false;
                if (i === '?') return undefined;
                throw new Error(`Bitmap.fromString: unknown symbol=${i}`);
            });
            if (width && row.length !== width) throw new Error(`Bitmap.fromString different row sizes: width=${width} cur=${row.length}`);
            width = row.length;
            data.push(row);
        }
        if (!width) width = 0;
        return new $631ab463e0c75d0e$export$3924f7882b5e2b61({
            height: height,
            width: width
        }, data);
    }
    data;
    height;
    width;
    constructor(size, data){
        const { height: height, width: width } = $631ab463e0c75d0e$export$3924f7882b5e2b61.size(size);
        this.data = data || Array.from({
            length: height
        }, ()=>$631ab463e0c75d0e$var$fillArr(width, undefined));
        this.height = height;
        this.width = width;
    }
    point(p) {
        return this.data[p.y][p.x];
    }
    isInside(p) {
        return 0 <= p.x && p.x < this.width && 0 <= p.y && p.y < this.height;
    }
    size(offset) {
        if (!offset) return {
            height: this.height,
            width: this.width
        };
        const { x: x, y: y } = this.xy(offset);
        return {
            height: this.height - y,
            width: this.width - x
        };
    }
    xy(c) {
        if (typeof c === 'number') c = {
            x: c,
            y: c
        };
        if (!Number.isSafeInteger(c.x)) throw new Error(`Bitmap: invalid x=${c.x}`);
        if (!Number.isSafeInteger(c.y)) throw new Error(`Bitmap: invalid y=${c.y}`);
        // Do modulo, so we can use negative positions
        c.x = $631ab463e0c75d0e$var$mod(c.x, this.width);
        c.y = $631ab463e0c75d0e$var$mod(c.y, this.height);
        return c;
    }
    // Basically every operation can be represented as rect
    rect(c, size, value) {
        const { x: x, y: y } = this.xy(c);
        const { height: height, width: width } = $631ab463e0c75d0e$export$3924f7882b5e2b61.size(size, this.size({
            x: x,
            y: y
        }));
        for(let yPos = 0; yPos < height; yPos++)for(let xPos = 0; xPos < width; xPos++)// NOTE: we use give function relative coordinates inside box
        this.data[y + yPos][x + xPos] = typeof value === 'function' ? value({
            x: xPos,
            y: yPos
        }, this.data[y + yPos][x + xPos]) : value;
        return this;
    }
    // returns rectangular part of bitmap
    rectRead(c, size, fn) {
        return this.rect(c, size, (c, cur)=>{
            fn(c, cur);
            return cur;
        });
    }
    // Horizontal & vertical lines
    hLine(c, len, value) {
        return this.rect(c, {
            width: len,
            height: 1
        }, value);
    }
    vLine(c, len, value) {
        return this.rect(c, {
            width: 1,
            height: len
        }, value);
    }
    // add border
    border(border = 2, value) {
        const height = this.height + 2 * border;
        const width = this.width + 2 * border;
        const v = $631ab463e0c75d0e$var$fillArr(border, value);
        const h = Array.from({
            length: border
        }, ()=>$631ab463e0c75d0e$var$fillArr(width, value));
        return new $631ab463e0c75d0e$export$3924f7882b5e2b61({
            height: height,
            width: width
        }, [
            ...h,
            ...this.data.map((i)=>[
                    ...v,
                    ...i,
                    ...v
                ]),
            ...h
        ]);
    }
    // Embed another bitmap on coordinates
    embed(c, bm) {
        return this.rect(c, bm.size(), ({ x: x, y: y })=>bm.data[y][x]);
    }
    // returns rectangular part of bitmap
    rectSlice(c, size = this.size()) {
        const rect = new $631ab463e0c75d0e$export$3924f7882b5e2b61($631ab463e0c75d0e$export$3924f7882b5e2b61.size(size, this.size(this.xy(c))));
        this.rect(c, size, ({ x: x, y: y }, cur)=>rect.data[y][x] = cur);
        return rect;
    }
    // Change shape, replace rows with columns (data[y][x] -> data[x][y])
    inverse() {
        const { height: height, width: width } = this;
        const res = new $631ab463e0c75d0e$export$3924f7882b5e2b61({
            height: width,
            width: height
        });
        return res.rect({
            x: 0,
            y: 0
        }, Infinity, ({ x: x, y: y })=>this.data[x][y]);
    }
    // Each pixel size is multiplied by factor
    scale(factor) {
        if (!Number.isSafeInteger(factor) || factor > 1024) throw new Error(`invalid scale factor: ${factor}`);
        const { height: height, width: width } = this;
        const res = new $631ab463e0c75d0e$export$3924f7882b5e2b61({
            height: factor * height,
            width: factor * width
        });
        return res.rect({
            x: 0,
            y: 0
        }, Infinity, ({ x: x, y: y })=>this.data[Math.floor(y / factor)][Math.floor(x / factor)]);
    }
    clone() {
        const res = new $631ab463e0c75d0e$export$3924f7882b5e2b61(this.size());
        return res.rect({
            x: 0,
            y: 0
        }, this.size(), ({ x: x, y: y })=>this.data[y][x]);
    }
    // Ensure that there is no undefined values left
    assertDrawn() {
        this.rectRead(0, Infinity, (_, cur)=>{
            if (typeof cur !== 'boolean') throw new Error(`Invalid color type=${typeof cur}`);
        });
    }
    // Simple string representation for debugging
    toString() {
        return this.data.map((i)=>i.map((j)=>j === undefined ? '?' : j ? 'X' : ' ').join('')).join(String.fromCharCode($631ab463e0c75d0e$var$chCodes.newline));
    }
    toASCII() {
        const { height: height, width: width, data: data } = this;
        let out = '';
        // Terminal character height is x2 of character width, so we process two rows of bitmap
        // to produce one row of ASCII
        for(let y = 0; y < height; y += 2){
            for(let x = 0; x < width; x++){
                const first = data[y][x];
                const second = y + 1 >= height ? true : data[y + 1][x]; // if last row outside bitmap, make it black
                if (!first && !second) out += "\u2588"; // both rows white (empty)
                else if (!first && second) out += "\u2580"; // top row white
                else if (first && !second) out += "\u2584"; // down row white
                else if (first && second) out += ' '; // both rows black
            }
            out += String.fromCharCode($631ab463e0c75d0e$var$chCodes.newline);
        }
        return out;
    }
    toTerm() {
        const cc = String.fromCharCode($631ab463e0c75d0e$var$chCodes.reset);
        const reset = cc + '[0m';
        const whiteBG = cc + '[1;47m  ' + reset;
        const darkBG = cc + `[40m  ` + reset;
        return this.data.map((i)=>i.map((j)=>j ? darkBG : whiteBG).join('')).join(String.fromCharCode($631ab463e0c75d0e$var$chCodes.newline));
    }
    toSVG(optimize = true) {
        let out = `<svg viewBox="0 0 ${this.width} ${this.height}" xmlns="http://www.w3.org/2000/svg">`;
        // Construct optimized SVG path data.
        let pathData = '';
        let prevPoint;
        this.rectRead(0, Infinity, (point, val)=>{
            if (!val) return;
            const { x: x, y: y } = point;
            if (!optimize) {
                out += `<rect x="${x}" y="${y}" width="1" height="1" />`;
                return;
            }
            // https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/d#path_commands
            // Determine the shortest way to represent the initial cursor movement.
            // M - Move cursor (without drawing) to absolute coordinate pair.
            let m = `M${x} ${y}`;
            // Only allow using the relative cursor move command if previous points
            // were drawn.
            if (prevPoint) {
                // m - Move cursor (without drawing) to relative coordinate pair.
                const relM = `m${x - prevPoint.x} ${y - prevPoint.y}`;
                if (relM.length <= m.length) m = relM;
            }
            // Determine the shortest way to represent the cell's bottom line draw.
            // H - Draw line from cursor position to absolute x coordinate.
            // h - Draw line from cursor position to relative x coordinate.
            const bH = x < 10 ? `H${x}` : 'h-1';
            // v - Draw line from cursor position to relative y coordinate.
            // Z - Close path (draws line from cursor position to M coordinate).
            pathData += `${m}h1v1${bH}Z`;
            prevPoint = point;
        });
        if (optimize) out += `<path d="${pathData}"/>`;
        out += `</svg>`;
        return out;
    }
    toGIF() {
        // NOTE: Small, but inefficient implementation.
        // Uses 1 byte per pixel.
        const u16le = (i)=>[
                i & 0xff,
                i >>> 8 & 0xff
            ];
        const dims = [
            ...u16le(this.width),
            ...u16le(this.height)
        ];
        const data = [];
        this.rectRead(0, Infinity, (_, cur)=>data.push(+(cur === true)));
        const N = 126; // Block size
        // prettier-ignore
        const bytes = [
            0x47,
            0x49,
            0x46,
            0x38,
            0x37,
            0x61,
            ...dims,
            0xf6,
            0x00,
            0x00,
            0xff,
            0xff,
            0xff,
            ...$631ab463e0c75d0e$var$fillArr(381, 0x00),
            0x2c,
            0x00,
            0x00,
            0x00,
            0x00,
            ...dims,
            0x00,
            0x07
        ];
        const fullChunks = Math.floor(data.length / N);
        // Full blocks
        for(let i = 0; i < fullChunks; i++)bytes.push(N + 1, 0x80, ...data.slice(N * i, N * (i + 1)).map((i)=>+i));
        // Remaining bytes
        bytes.push(data.length % N + 1, 0x80, ...data.slice(fullChunks * N).map((i)=>+i));
        bytes.push(0x01, 0x81, 0x00, 0x3b);
        return new Uint8Array(bytes);
    }
    toImage(isRGB = false) {
        const { height: height, width: width } = this.size();
        const data = new Uint8Array(height * width * (isRGB ? 3 : 4));
        let i = 0;
        for(let y = 0; y < height; y++)for(let x = 0; x < width; x++){
            const value = !!this.data[y][x] ? 0 : 255;
            data[i++] = value;
            data[i++] = value;
            data[i++] = value;
            if (!isRGB) data[i++] = 255; // alpha channel
        }
        return {
            height: height,
            width: width,
            data: data
        };
    }
}
const $631ab463e0c75d0e$export$4e93d4176068ea89 = [
    'low',
    'medium',
    'quartile',
    'high'
];
const $631ab463e0c75d0e$export$72958ca7c468c63b = [
    'numeric',
    'alphanumeric',
    'byte',
    'kanji',
    'eci'
];
// Various constants & tables
// prettier-ignore
const $631ab463e0c75d0e$var$BYTES = [
    // 1,  2,  3,   4,   5,   6,   7,   8,   9,  10,  11,  12,  13,  14,  15,  16,  17,  18,  19,   20,
    26,
    44,
    70,
    100,
    134,
    172,
    196,
    242,
    292,
    346,
    404,
    466,
    532,
    581,
    655,
    733,
    815,
    901,
    991,
    1085,
    //  21,   22,   23,   24,   25,   26,   27,   28,   29,   30,   31,   32,   33,   34,   35,   36,   37,   38,   39,   40
    1156,
    1258,
    1364,
    1474,
    1588,
    1706,
    1828,
    1921,
    2051,
    2185,
    2323,
    2465,
    2611,
    2761,
    2876,
    3034,
    3196,
    3362,
    3532,
    3706
];
// prettier-ignore
const $631ab463e0c75d0e$var$WORDS_PER_BLOCK = {
    // Version 1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40
    low: [
        7,
        10,
        15,
        20,
        26,
        18,
        20,
        24,
        30,
        18,
        20,
        24,
        26,
        30,
        22,
        24,
        28,
        30,
        28,
        28,
        28,
        28,
        30,
        30,
        26,
        28,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30
    ],
    medium: [
        10,
        16,
        26,
        18,
        24,
        16,
        18,
        22,
        22,
        26,
        30,
        22,
        22,
        24,
        24,
        28,
        28,
        26,
        26,
        26,
        26,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28,
        28
    ],
    quartile: [
        13,
        22,
        18,
        26,
        18,
        24,
        18,
        22,
        20,
        24,
        28,
        26,
        24,
        20,
        30,
        24,
        28,
        28,
        26,
        30,
        28,
        30,
        30,
        30,
        30,
        28,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30
    ],
    high: [
        17,
        28,
        22,
        16,
        22,
        28,
        26,
        26,
        24,
        28,
        24,
        28,
        22,
        24,
        24,
        30,
        28,
        28,
        26,
        28,
        30,
        24,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30,
        30
    ]
};
// prettier-ignore
const $631ab463e0c75d0e$var$ECC_BLOCKS = {
    // Version   1, 2, 3, 4, 5, 6, 7, 8, 9,10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40
    low: [
        1,
        1,
        1,
        1,
        1,
        2,
        2,
        2,
        2,
        4,
        4,
        4,
        4,
        4,
        6,
        6,
        6,
        6,
        7,
        8,
        8,
        9,
        9,
        10,
        12,
        12,
        12,
        13,
        14,
        15,
        16,
        17,
        18,
        19,
        19,
        20,
        21,
        22,
        24,
        25
    ],
    medium: [
        1,
        1,
        1,
        2,
        2,
        4,
        4,
        4,
        5,
        5,
        5,
        8,
        9,
        9,
        10,
        10,
        11,
        13,
        14,
        16,
        17,
        17,
        18,
        20,
        21,
        23,
        25,
        26,
        28,
        29,
        31,
        33,
        35,
        37,
        38,
        40,
        43,
        45,
        47,
        49
    ],
    quartile: [
        1,
        1,
        2,
        2,
        4,
        4,
        6,
        6,
        8,
        8,
        8,
        10,
        12,
        16,
        12,
        17,
        16,
        18,
        21,
        20,
        23,
        23,
        25,
        27,
        29,
        34,
        34,
        35,
        38,
        40,
        43,
        45,
        48,
        51,
        53,
        56,
        59,
        62,
        65,
        68
    ],
    high: [
        1,
        1,
        2,
        4,
        4,
        4,
        5,
        6,
        8,
        8,
        11,
        11,
        16,
        16,
        18,
        16,
        19,
        21,
        25,
        25,
        25,
        34,
        30,
        32,
        35,
        37,
        40,
        42,
        45,
        48,
        51,
        54,
        57,
        60,
        63,
        66,
        70,
        74,
        77,
        81
    ]
};
const $631ab463e0c75d0e$var$info = {
    size: {
        encode: (ver)=>21 + 4 * (ver - 1),
        decode: (size)=>(size - 17) / 4
    },
    sizeType: (ver)=>Math.floor((ver + 7) / 17),
    // Based on https://codereview.stackexchange.com/questions/74925/algorithm-to-generate-this-alignment-pattern-locations-table-for-qr-codes
    alignmentPatterns (ver) {
        if (ver === 1) return [];
        const first = 6;
        const last = $631ab463e0c75d0e$var$info.size.encode(ver) - first - 1;
        const distance = last - first;
        const count = Math.ceil(distance / 28);
        let interval = Math.floor(distance / count);
        if (interval % 2) interval += 1;
        else if (distance % count * 2 >= count) interval += 2;
        const res = [
            first
        ];
        for(let m = 1; m < count; m++)res.push(last - (count - m) * interval);
        res.push(last);
        return res;
    },
    ECCode: {
        low: 1,
        medium: 0,
        quartile: 3,
        high: 2
    },
    formatMask: 21522,
    formatBits (ecc, maskIdx) {
        const data = $631ab463e0c75d0e$var$info.ECCode[ecc] << 3 | maskIdx;
        let d = data;
        for(let i = 0; i < 10; i++)d = d << 1 ^ (d >> 9) * 1335;
        return (data << 10 | d) ^ $631ab463e0c75d0e$var$info.formatMask;
    },
    versionBits (ver) {
        let d = ver;
        for(let i = 0; i < 12; i++)d = d << 1 ^ (d >> 11) * 7973;
        return ver << 12 | d;
    },
    alphabet: {
        numeric: $631ab463e0c75d0e$var$alphabet('0123456789'),
        alphanumerc: $631ab463e0c75d0e$var$alphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:')
    },
    lengthBits (ver, type) {
        const table = {
            numeric: [
                10,
                12,
                14
            ],
            alphanumeric: [
                9,
                11,
                13
            ],
            byte: [
                8,
                16,
                16
            ],
            kanji: [
                8,
                10,
                12
            ],
            eci: [
                0,
                0,
                0
            ]
        };
        return table[type][$631ab463e0c75d0e$var$info.sizeType(ver)];
    },
    modeBits: {
        numeric: '0001',
        alphanumeric: '0010',
        byte: '0100',
        kanji: '1000',
        eci: '0111'
    },
    capacity (ver, ecc) {
        const bytes = $631ab463e0c75d0e$var$BYTES[ver - 1];
        const words = $631ab463e0c75d0e$var$WORDS_PER_BLOCK[ecc][ver - 1];
        const numBlocks = $631ab463e0c75d0e$var$ECC_BLOCKS[ecc][ver - 1];
        const blockLen = Math.floor(bytes / numBlocks) - words;
        const shortBlocks = numBlocks - bytes % numBlocks;
        return {
            words: words,
            numBlocks: numBlocks,
            shortBlocks: shortBlocks,
            blockLen: blockLen,
            capacity: (bytes - words * numBlocks) * 8,
            total: (words + blockLen) * numBlocks + numBlocks - shortBlocks
        };
    }
};
const $631ab463e0c75d0e$var$PATTERNS = [
    (x, y)=>(x + y) % 2 == 0,
    (_x, y)=>y % 2 == 0,
    (x, _y)=>x % 3 == 0,
    (x, y)=>(x + y) % 3 == 0,
    (x, y)=>(Math.floor(y / 2) + Math.floor(x / 3)) % 2 == 0,
    (x, y)=>x * y % 2 + x * y % 3 == 0,
    (x, y)=>(x * y % 2 + x * y % 3) % 2 == 0,
    (x, y)=>((x + y) % 2 + x * y % 3) % 2 == 0
];
// Galois field && reed-solomon encoding
const $631ab463e0c75d0e$var$GF = {
    tables: ((p_poly)=>{
        const exp = $631ab463e0c75d0e$var$fillArr(256, 0);
        const log = $631ab463e0c75d0e$var$fillArr(256, 0);
        for(let i = 0, x = 1; i < 256; i++){
            exp[i] = x;
            log[x] = i;
            x <<= 1;
            if (x & 0x100) x ^= p_poly;
        }
        return {
            exp: exp,
            log: log
        };
    })(0x11d),
    exp: (x)=>$631ab463e0c75d0e$var$GF.tables.exp[x],
    log (x) {
        if (x === 0) throw new Error(`GF.log: invalid arg=${x}`);
        return $631ab463e0c75d0e$var$GF.tables.log[x] % 255;
    },
    mul (x, y) {
        if (x === 0 || y === 0) return 0;
        return $631ab463e0c75d0e$var$GF.tables.exp[($631ab463e0c75d0e$var$GF.tables.log[x] + $631ab463e0c75d0e$var$GF.tables.log[y]) % 255];
    },
    add: (x, y)=>x ^ y,
    pow: (x, e)=>$631ab463e0c75d0e$var$GF.tables.exp[$631ab463e0c75d0e$var$GF.tables.log[x] * e % 255],
    inv (x) {
        if (x === 0) throw new Error(`GF.inverse: invalid arg=${x}`);
        return $631ab463e0c75d0e$var$GF.tables.exp[255 - $631ab463e0c75d0e$var$GF.tables.log[x]];
    },
    polynomial (poly) {
        if (poly.length == 0) throw new Error('GF.polymomial: invalid length');
        if (poly[0] !== 0) return poly;
        // Strip leading zeros
        let i = 0;
        for(; i < poly.length - 1 && poly[i] == 0; i++);
        return poly.slice(i);
    },
    monomial (degree, coefficient) {
        if (degree < 0) throw new Error(`GF.monomial: invalid degree=${degree}`);
        if (coefficient == 0) return [
            0
        ];
        let coefficients = $631ab463e0c75d0e$var$fillArr(degree + 1, 0);
        coefficients[0] = coefficient;
        return $631ab463e0c75d0e$var$GF.polynomial(coefficients);
    },
    degree: (a)=>a.length - 1,
    coefficient: (a, degree)=>a[$631ab463e0c75d0e$var$GF.degree(a) - degree],
    mulPoly (a, b) {
        if (a[0] === 0 || b[0] === 0) return [
            0
        ];
        const res = $631ab463e0c75d0e$var$fillArr(a.length + b.length - 1, 0);
        for(let i = 0; i < a.length; i++)for(let j = 0; j < b.length; j++)res[i + j] = $631ab463e0c75d0e$var$GF.add(res[i + j], $631ab463e0c75d0e$var$GF.mul(a[i], b[j]));
        return $631ab463e0c75d0e$var$GF.polynomial(res);
    },
    mulPolyScalar (a, scalar) {
        if (scalar == 0) return [
            0
        ];
        if (scalar == 1) return a;
        const res = $631ab463e0c75d0e$var$fillArr(a.length, 0);
        for(let i = 0; i < a.length; i++)res[i] = $631ab463e0c75d0e$var$GF.mul(a[i], scalar);
        return $631ab463e0c75d0e$var$GF.polynomial(res);
    },
    mulPolyMonomial (a, degree, coefficient) {
        if (degree < 0) throw new Error('GF.mulPolyMonomial: invalid degree');
        if (coefficient == 0) return [
            0
        ];
        const res = $631ab463e0c75d0e$var$fillArr(a.length + degree, 0);
        for(let i = 0; i < a.length; i++)res[i] = $631ab463e0c75d0e$var$GF.mul(a[i], coefficient);
        return $631ab463e0c75d0e$var$GF.polynomial(res);
    },
    addPoly (a, b) {
        if (a[0] === 0) return b;
        if (b[0] === 0) return a;
        let smaller = a;
        let larger = b;
        if (smaller.length > larger.length) [smaller, larger] = [
            larger,
            smaller
        ];
        let sumDiff = $631ab463e0c75d0e$var$fillArr(larger.length, 0);
        let lengthDiff = larger.length - smaller.length;
        let s = larger.slice(0, lengthDiff);
        for(let i = 0; i < s.length; i++)sumDiff[i] = s[i];
        for(let i = lengthDiff; i < larger.length; i++)sumDiff[i] = $631ab463e0c75d0e$var$GF.add(smaller[i - lengthDiff], larger[i]);
        return $631ab463e0c75d0e$var$GF.polynomial(sumDiff);
    },
    remainderPoly (data, divisor) {
        const out = Array.from(data);
        for(let i = 0; i < data.length - divisor.length + 1; i++){
            const elm = out[i];
            if (elm === 0) continue;
            for(let j = 1; j < divisor.length; j++)if (divisor[j] !== 0) out[i + j] = $631ab463e0c75d0e$var$GF.add(out[i + j], $631ab463e0c75d0e$var$GF.mul(divisor[j], elm));
        }
        return out.slice(data.length - divisor.length + 1, out.length);
    },
    divisorPoly (degree) {
        let g = [
            1
        ];
        for(let i = 0; i < degree; i++)g = $631ab463e0c75d0e$var$GF.mulPoly(g, [
            1,
            $631ab463e0c75d0e$var$GF.pow(2, i)
        ]);
        return g;
    },
    evalPoly (poly, a) {
        if (a == 0) return $631ab463e0c75d0e$var$GF.coefficient(poly, 0); // Just return the x^0 coefficient
        let res = poly[0];
        for(let i = 1; i < poly.length; i++)res = $631ab463e0c75d0e$var$GF.add($631ab463e0c75d0e$var$GF.mul(a, res), poly[i]);
        return res;
    },
    // TODO: cleanup
    euclidian (a, b, R) {
        // Force degree(a) >= degree(b)
        if ($631ab463e0c75d0e$var$GF.degree(a) < $631ab463e0c75d0e$var$GF.degree(b)) [a, b] = [
            b,
            a
        ];
        let rLast = a;
        let r = b;
        let tLast = [
            0
        ];
        let t = [
            1
        ];
        // while degree of Ri ≥ t/2
        while(2 * $631ab463e0c75d0e$var$GF.degree(r) >= R){
            let rLastLast = rLast;
            let tLastLast = tLast;
            rLast = r;
            tLast = t;
            if (rLast[0] === 0) throw new Error('rLast[0] === 0');
            r = rLastLast;
            let q = [
                0
            ];
            const dltInverse = $631ab463e0c75d0e$var$GF.inv(rLast[0]);
            while($631ab463e0c75d0e$var$GF.degree(r) >= $631ab463e0c75d0e$var$GF.degree(rLast) && r[0] !== 0){
                const degreeDiff = $631ab463e0c75d0e$var$GF.degree(r) - $631ab463e0c75d0e$var$GF.degree(rLast);
                const scale = $631ab463e0c75d0e$var$GF.mul(r[0], dltInverse);
                q = $631ab463e0c75d0e$var$GF.addPoly(q, $631ab463e0c75d0e$var$GF.monomial(degreeDiff, scale));
                r = $631ab463e0c75d0e$var$GF.addPoly(r, $631ab463e0c75d0e$var$GF.mulPolyMonomial(rLast, degreeDiff, scale));
            }
            q = $631ab463e0c75d0e$var$GF.mulPoly(q, tLast);
            t = $631ab463e0c75d0e$var$GF.addPoly(q, tLastLast);
            if ($631ab463e0c75d0e$var$GF.degree(r) >= $631ab463e0c75d0e$var$GF.degree(rLast)) throw new Error(`Division failed r: ${r}, rLast: ${rLast}`);
        }
        const sigmaTildeAtZero = $631ab463e0c75d0e$var$GF.coefficient(t, 0);
        if (sigmaTildeAtZero == 0) throw new Error('sigmaTilde(0) was zero');
        const inverse = $631ab463e0c75d0e$var$GF.inv(sigmaTildeAtZero);
        return [
            $631ab463e0c75d0e$var$GF.mulPolyScalar(t, inverse),
            $631ab463e0c75d0e$var$GF.mulPolyScalar(r, inverse)
        ];
    }
};
function $631ab463e0c75d0e$var$RS(eccWords) {
    return {
        encode (from) {
            const d = $631ab463e0c75d0e$var$GF.divisorPoly(eccWords);
            const pol = Array.from(from);
            pol.push(...d.slice(0, -1).fill(0));
            return Uint8Array.from($631ab463e0c75d0e$var$GF.remainderPoly(pol, d));
        },
        decode (to) {
            const res = to.slice();
            const poly = $631ab463e0c75d0e$var$GF.polynomial(Array.from(to));
            // Find errors
            let syndrome = $631ab463e0c75d0e$var$fillArr(eccWords, 0);
            let hasError = false;
            for(let i = 0; i < eccWords; i++){
                const evl = $631ab463e0c75d0e$var$GF.evalPoly(poly, $631ab463e0c75d0e$var$GF.exp(i));
                syndrome[syndrome.length - 1 - i] = evl;
                if (evl !== 0) hasError = true;
            }
            if (!hasError) return res;
            syndrome = $631ab463e0c75d0e$var$GF.polynomial(syndrome);
            const monomial = $631ab463e0c75d0e$var$GF.monomial(eccWords, 1);
            const [errorLocator, errorEvaluator] = $631ab463e0c75d0e$var$GF.euclidian(monomial, syndrome, eccWords);
            // Error locations
            const locations = $631ab463e0c75d0e$var$fillArr($631ab463e0c75d0e$var$GF.degree(errorLocator), 0);
            let e = 0;
            for(let i = 1; i < 256 && e < locations.length; i++)if ($631ab463e0c75d0e$var$GF.evalPoly(errorLocator, i) === 0) locations[e++] = $631ab463e0c75d0e$var$GF.inv(i);
            if (e !== locations.length) throw new Error('RS.decode: invalid errors number');
            for(let i = 0; i < locations.length; i++){
                const pos = res.length - 1 - $631ab463e0c75d0e$var$GF.log(locations[i]);
                if (pos < 0) throw new Error('RS.decode: invalid error location');
                const xiInverse = $631ab463e0c75d0e$var$GF.inv(locations[i]);
                let denominator = 1;
                for(let j = 0; j < locations.length; j++){
                    if (i === j) continue;
                    denominator = $631ab463e0c75d0e$var$GF.mul(denominator, $631ab463e0c75d0e$var$GF.add(1, $631ab463e0c75d0e$var$GF.mul(locations[j], xiInverse)));
                }
                res[pos] = $631ab463e0c75d0e$var$GF.add(res[pos], $631ab463e0c75d0e$var$GF.mul($631ab463e0c75d0e$var$GF.evalPoly(errorEvaluator, xiInverse), $631ab463e0c75d0e$var$GF.inv(denominator)));
            }
            return res;
        }
    };
}
// Interleaves blocks
function $631ab463e0c75d0e$var$interleave(ver, ecc) {
    const { words: words, shortBlocks: shortBlocks, numBlocks: numBlocks, blockLen: blockLen, total: total } = $631ab463e0c75d0e$var$info.capacity(ver, ecc);
    const rs = $631ab463e0c75d0e$var$RS(words);
    return {
        encode (bytes) {
            // Add error correction to bytes
            const blocks = [];
            const eccBlocks = [];
            for(let i = 0; i < numBlocks; i++){
                const isShort = i < shortBlocks;
                const len = blockLen + (isShort ? 0 : 1);
                blocks.push(bytes.subarray(0, len));
                eccBlocks.push(rs.encode(bytes.subarray(0, len)));
                bytes = bytes.subarray(len);
            }
            const resBlocks = $631ab463e0c75d0e$var$interleaveBytes(...blocks);
            const resECC = $631ab463e0c75d0e$var$interleaveBytes(...eccBlocks);
            const res = new Uint8Array(resBlocks.length + resECC.length);
            res.set(resBlocks);
            res.set(resECC, resBlocks.length);
            return res;
        },
        decode (data) {
            if (data.length !== total) throw new Error(`interleave.decode: len(data)=${data.length}, total=${total}`);
            const blocks = [];
            for(let i = 0; i < numBlocks; i++){
                const isShort = i < shortBlocks;
                blocks.push(new Uint8Array(words + blockLen + (isShort ? 0 : 1)));
            }
            // Short blocks
            let pos = 0;
            for(let i = 0; i < blockLen; i++)for(let j = 0; j < numBlocks; j++)blocks[j][i] = data[pos++];
            // Long blocks
            for(let j = shortBlocks; j < numBlocks; j++)blocks[j][blockLen] = data[pos++];
            // ECC
            for(let i = blockLen; i < blockLen + words; i++)for(let j = 0; j < numBlocks; j++){
                const isShort = j < shortBlocks;
                blocks[j][i + (isShort ? 0 : 1)] = data[pos++];
            }
            // Decode
            // Error-correct and copy data blocks together into a stream of bytes
            const res = [];
            for (const block of blocks)res.push(...Array.from(rs.decode(block)).slice(0, -words));
            return Uint8Array.from(res);
        }
    };
}
// Draw
// Generic template per version+ecc+mask. Can be cached, to speedup calculations.
function $631ab463e0c75d0e$var$drawTemplate(ver, ecc, maskIdx, test = false) {
    const size = $631ab463e0c75d0e$var$info.size.encode(ver);
    let b = new $631ab463e0c75d0e$export$3924f7882b5e2b61(size + 2);
    // Finder patterns
    // We draw full pattern and later slice, since before addition of borders finder is truncated by one pixel on sides
    const finder = new $631ab463e0c75d0e$export$3924f7882b5e2b61(3).rect(0, 3, true).border(1, false).border(1, true).border(1, false);
    b = b.embed(0, finder) // top left
    .embed({
        x: -finder.width,
        y: 0
    }, finder) // top right
    .embed({
        x: 0,
        y: -finder.height
    }, finder); // bottom left
    b = b.rectSlice(1, size);
    // Alignment patterns
    const align = new $631ab463e0c75d0e$export$3924f7882b5e2b61(1).rect(0, 1, true).border(1, false).border(1, true);
    const alignPos = $631ab463e0c75d0e$var$info.alignmentPatterns(ver);
    for (const y of alignPos)for (const x of alignPos){
        if (b.data[y][x] !== undefined) continue;
        b.embed({
            x: x - 2,
            y: y - 2
        }, align); // center of pattern should be at position
    }
    // Timing patterns
    b = b.hLine({
        x: 0,
        y: 6
    }, Infinity, ({ x: x }, cur)=>cur === undefined ? x % 2 == 0 : cur).vLine({
        x: 6,
        y: 0
    }, Infinity, ({ y: y }, cur)=>cur === undefined ? y % 2 == 0 : cur);
    // Format information
    {
        const bits = $631ab463e0c75d0e$var$info.formatBits(ecc, maskIdx);
        const getBit = (i)=>!test && (bits >> i & 1) == 1;
        // vertical
        for(let i = 0; i < 6; i++)b.data[i][8] = getBit(i); // right of top-left finder
        // TODO: re-write as lines, like:
        // b.vLine({ x: 8, y: 0 }, 6, ({ x, y }) => getBit(y));
        for(let i = 6; i < 8; i++)b.data[i + 1][8] = getBit(i); // after timing pattern
        for(let i = 8; i < 15; i++)b.data[size - 15 + i][8] = getBit(i); // right of bottom-left finder
        // horizontal
        for(let i = 0; i < 8; i++)b.data[8][size - i - 1] = getBit(i); // under top-right finder
        for(let i = 8; i < 9; i++)b.data[8][15 - i - 1 + 1] = getBit(i); // VVV, after timing
        for(let i = 9; i < 15; i++)b.data[8][15 - i - 1] = getBit(i); // under top-left finder
        b.data[size - 8][8] = !test; // bottom-left finder, right
    }
    // Version information
    if (ver >= 7) {
        const bits = $631ab463e0c75d0e$var$info.versionBits(ver);
        for(let i = 0; i < 18; i += 1){
            const bit = !test && (bits >> i & 1) == 1;
            const x = Math.floor(i / 3);
            const y = i % 3 + size - 8 - 3;
            // two copies
            b.data[x][y] = bit;
            b.data[y][x] = bit;
        }
    }
    return b;
}
// zigzag: bottom->top && top->bottom
function $631ab463e0c75d0e$var$zigzag(tpl, maskIdx, fn) {
    const size = tpl.height;
    const pattern = $631ab463e0c75d0e$var$PATTERNS[maskIdx];
    // zig-zag pattern
    let dir = -1;
    let y = size - 1;
    // two columns at time
    for(let xOffset = size - 1; xOffset > 0; xOffset -= 2){
        if (xOffset == 6) xOffset = 5; // skip vertical timing pattern
        for(;; y += dir){
            for(let j = 0; j < 2; j += 1){
                const x = xOffset - j;
                if (tpl.data[y][x] !== undefined) continue; // skip already written elements
                fn(x, y, pattern(x, y));
            }
            if (y + dir < 0 || y + dir >= size) break;
        }
        dir = -dir; // change direction
    }
}
// NOTE: byte encoding is just representation, QR works with strings only. Most decoders will fail on raw byte array,
// since they expect unicode or other text encoding inside bytes
function $631ab463e0c75d0e$var$detectType(str) {
    let type = 'numeric';
    for (let x of str){
        if ($631ab463e0c75d0e$var$info.alphabet.numeric.has(x)) continue;
        type = 'alphanumeric';
        if (!$631ab463e0c75d0e$var$info.alphabet.alphanumerc.has(x)) return 'byte';
    }
    return type;
}
function $631ab463e0c75d0e$export$3df70a15530b5e81(str) {
    if (typeof str !== 'string') throw new Error(`utf8ToBytes expected string, got ${typeof str}`);
    return new Uint8Array(new TextEncoder().encode(str)); // https://bugzil.la/1681809
}
function $631ab463e0c75d0e$var$encode(ver, ecc, data, type) {
    let encoded = '';
    let dataLen = data.length;
    if (type === 'numeric') {
        const t = $631ab463e0c75d0e$var$info.alphabet.numeric.decode(data.split(''));
        const n = t.length;
        for(let i = 0; i < n - 2; i += 3)encoded += $631ab463e0c75d0e$var$bin(t[i] * 100 + t[i + 1] * 10 + t[i + 2], 10);
        if (n % 3 === 1) encoded += $631ab463e0c75d0e$var$bin(t[n - 1], 4);
        else if (n % 3 === 2) encoded += $631ab463e0c75d0e$var$bin(t[n - 2] * 10 + t[n - 1], 7);
    } else if (type === 'alphanumeric') {
        const t = $631ab463e0c75d0e$var$info.alphabet.alphanumerc.decode(data.split(''));
        const n = t.length;
        for(let i = 0; i < n - 1; i += 2)encoded += $631ab463e0c75d0e$var$bin(t[i] * 45 + t[i + 1], 11);
        if (n % 2 == 1) encoded += $631ab463e0c75d0e$var$bin(t[n - 1], 6); // pad if odd number of chars
    } else if (type === 'byte') {
        const utf8 = $631ab463e0c75d0e$export$3df70a15530b5e81(data);
        dataLen = utf8.length;
        encoded = Array.from(utf8).map((i)=>$631ab463e0c75d0e$var$bin(i, 8)).join('');
    } else throw new Error('encode: unsupported type');
    const { capacity: capacity } = $631ab463e0c75d0e$var$info.capacity(ver, ecc);
    const len = $631ab463e0c75d0e$var$bin(dataLen, $631ab463e0c75d0e$var$info.lengthBits(ver, type));
    let bits = $631ab463e0c75d0e$var$info.modeBits[type] + len + encoded;
    if (bits.length > capacity) throw new Error('Capacity overflow');
    // Terminator
    bits += '0'.repeat(Math.min(4, Math.max(0, capacity - bits.length)));
    // Pad bits string untill full byte
    if (bits.length % 8) bits += '0'.repeat(8 - bits.length % 8);
    // Add padding until capacity is full
    const padding = '1110110000010001';
    for(let idx = 0; bits.length !== capacity; idx++)bits += padding[idx % padding.length];
    // Convert a bitstring to array of bytes
    const bytes = Uint8Array.from(bits.match(/(.{8})/g).map((i)=>Number(`0b${i}`)));
    return $631ab463e0c75d0e$var$interleave(ver, ecc).encode(bytes);
}
// DRAW
function $631ab463e0c75d0e$var$drawQR(ver, ecc, data, maskIdx, test = false) {
    const b = $631ab463e0c75d0e$var$drawTemplate(ver, ecc, maskIdx, test);
    let i = 0;
    const need = 8 * data.length;
    $631ab463e0c75d0e$var$zigzag(b, maskIdx, (x, y, mask)=>{
        let value = false;
        if (i < need) {
            value = (data[i >>> 3] >> (7 - i & 7) & 1) !== 0;
            i++;
        }
        b.data[y][x] = value !== mask; // !== as xor
    });
    if (i !== need) throw new Error('QR: bytes left after draw');
    return b;
}
function $631ab463e0c75d0e$var$penalty(bm) {
    const inverse = bm.inverse();
    // Adjacent modules in row/column in same | No. of modules = (5 + i) color
    const sameColor = (row)=>{
        let res = 0;
        for(let i = 0, same = 1, last = undefined; i < row.length; i++){
            if (last === row[i]) {
                same++;
                if (i !== row.length - 1) continue; // handle last element
            }
            if (same >= 5) res += 3 + (same - 5);
            last = row[i];
            same = 1;
        }
        return res;
    };
    let adjacent = 0;
    bm.data.forEach((row)=>adjacent += sameColor(row));
    inverse.data.forEach((column)=>adjacent += sameColor(column));
    // Block of modules in same color (Block size = 2x2)
    let box = 0;
    let b = bm.data;
    const lastW = bm.width - 1;
    const lastH = bm.height - 1;
    for(let x = 0; x < lastW; x++)for(let y = 0; y < lastH; y++){
        const x1 = x + 1;
        const y1 = y + 1;
        if (b[x][y] === b[x1][y] && b[x1][y] === b[x][y1] && b[x1][y] === b[x1][y1]) box += 3;
    }
    // 1:1:3:1:1 ratio (dark:light:dark:light:dark) pattern in row/column, preceded or followed by light area 4 modules wide
    const finderPattern = (row)=>{
        const finderPattern = [
            true,
            false,
            true,
            true,
            true,
            false,
            true
        ]; // dark:light:dark:light:dark
        const lightPattern = [
            false,
            false,
            false,
            false
        ]; // light area 4 modules wide
        const p1 = [
            ...finderPattern,
            ...lightPattern
        ];
        const p2 = [
            ...lightPattern,
            ...finderPattern
        ];
        let res = 0;
        for(let i = 0; i < row.length; i++){
            if ($631ab463e0c75d0e$var$includesAt(row, p1, i)) res += 40;
            if ($631ab463e0c75d0e$var$includesAt(row, p2, i)) res += 40;
        }
        return res;
    };
    let finder = 0;
    for (const row of bm.data)finder += finderPattern(row);
    for (const column of inverse.data)finder += finderPattern(column);
    // Proportion of dark modules in entire symbol
    // Add 10 points to a deviation of 5% increment or decrement in the proportion
    // ratio of dark module from the referential 50%
    let darkPixels = 0;
    bm.rectRead(0, Infinity, (_c, val)=>darkPixels += val ? 1 : 0);
    const darkPercent = darkPixels / (bm.height * bm.width) * 100;
    const dark = 10 * Math.floor(Math.abs(darkPercent - 50) / 5);
    return adjacent + box + finder + dark;
}
// Selects best mask according to penalty, if no mask is provided
function $631ab463e0c75d0e$var$drawQRBest(ver, ecc, data, maskIdx) {
    if (maskIdx === undefined) {
        const bestMask = $631ab463e0c75d0e$var$best();
        for(let mask = 0; mask < $631ab463e0c75d0e$var$PATTERNS.length; mask++)bestMask.add($631ab463e0c75d0e$var$penalty($631ab463e0c75d0e$var$drawQR(ver, ecc, data, mask, true)), mask);
        maskIdx = bestMask.get();
    }
    if (maskIdx === undefined) throw new Error('Cannot find mask'); // Should never happen
    return $631ab463e0c75d0e$var$drawQR(ver, ecc, data, maskIdx);
}
function $631ab463e0c75d0e$var$validateECC(ec) {
    if (!$631ab463e0c75d0e$export$4e93d4176068ea89.includes(ec)) throw new Error(`Invalid error correction mode=${ec}. Expected: ${$631ab463e0c75d0e$export$4e93d4176068ea89}`);
}
function $631ab463e0c75d0e$var$validateEncoding(enc) {
    if (!$631ab463e0c75d0e$export$72958ca7c468c63b.includes(enc)) throw new Error(`Encoding: invalid mode=${enc}. Expected: ${$631ab463e0c75d0e$export$72958ca7c468c63b}`);
    if (enc === 'kanji' || enc === 'eci') throw new Error(`Encoding: ${enc} is not supported (yet?).`);
}
function $631ab463e0c75d0e$var$validateMask(mask) {
    if (![
        0,
        1,
        2,
        3,
        4,
        5,
        6,
        7
    ].includes(mask) || !$631ab463e0c75d0e$var$PATTERNS[mask]) throw new Error(`Invalid mask=${mask}. Expected number [0..7]`);
}
function $631ab463e0c75d0e$export$4fd08dab997cce30(text, output = 'raw', opts = {}) {
    const ecc = opts.ecc !== undefined ? opts.ecc : 'medium';
    $631ab463e0c75d0e$var$validateECC(ecc);
    const encoding = opts.encoding !== undefined ? opts.encoding : $631ab463e0c75d0e$var$detectType(text);
    $631ab463e0c75d0e$var$validateEncoding(encoding);
    if (opts.mask !== undefined) $631ab463e0c75d0e$var$validateMask(opts.mask);
    let ver = opts.version;
    let data, err = new Error('Unknown error');
    if (ver !== undefined) {
        $631ab463e0c75d0e$var$validateVersion(ver);
        data = $631ab463e0c75d0e$var$encode(ver, ecc, text, encoding);
    } else {
        // If no version is provided, try to find smallest one which fits
        // Currently just scans all version, can be significantly speedup if needed
        for(let i = 1; i <= 40; i++)try {
            data = $631ab463e0c75d0e$var$encode(i, ecc, text, encoding);
            ver = i;
            break;
        } catch (e) {
            err = e;
        }
    }
    if (!ver || !data) throw err;
    let res = $631ab463e0c75d0e$var$drawQRBest(ver, ecc, data, opts.mask);
    res.assertDrawn();
    const border = opts.border === undefined ? 2 : opts.border;
    if (!Number.isSafeInteger(border)) throw new Error(`invalid border type=${typeof border}`);
    res = res.border(border, false); // Add border
    if (opts.scale !== undefined) res = res.scale(opts.scale); // Scale image
    if (output === 'raw') return res.data;
    else if (output === 'ascii') return res.toASCII();
    else if (output === 'svg') return res.toSVG(opts.optimize);
    else if (output === 'gif') return res.toGIF();
    else if (output === 'term') return res.toTerm();
    else throw new Error(`Unknown output: ${output}`);
}
var $631ab463e0c75d0e$export$2e2bcd8739ae039 = $631ab463e0c75d0e$export$4fd08dab997cce30;
const $631ab463e0c75d0e$export$eab97d15b1788b8d = {
    best: $631ab463e0c75d0e$var$best,
    bin: $631ab463e0c75d0e$var$bin,
    drawTemplate: $631ab463e0c75d0e$var$drawTemplate,
    fillArr: $631ab463e0c75d0e$var$fillArr,
    info: $631ab463e0c75d0e$var$info,
    interleave: $631ab463e0c75d0e$var$interleave,
    validateVersion: $631ab463e0c75d0e$var$validateVersion,
    zigzag: $631ab463e0c75d0e$var$zigzag
};
const $631ab463e0c75d0e$export$dfcf182abe71b376 = {
    Bitmap: $631ab463e0c75d0e$export$3924f7882b5e2b61,
    info: $631ab463e0c75d0e$var$info,
    detectType: $631ab463e0c75d0e$var$detectType,
    encode: $631ab463e0c75d0e$var$encode,
    drawQR: $631ab463e0c75d0e$var$drawQR,
    penalty: $631ab463e0c75d0e$var$penalty,
    PATTERNS: $631ab463e0c75d0e$var$PATTERNS
}; // Type tests
 // const o1 = qr('test', 'ascii');
 // const o2 = qr('test', 'raw');
 // const o3 = qr('test', 'gif');
 // const o4 = qr('test', 'svg');
 // const o5 = qr('test', 'term');
 //# sourceMappingURL=index.js.map



const $1763b227c722b862$var$advanced = document.getElementById("advanced");
$1763b227c722b862$var$advanced.addEventListener("change", $1763b227c722b862$var$update);
function $1763b227c722b862$var$update() {
    if ($1763b227c722b862$var$advanced.checked) document.body.classList.add("show-advanced");
    else document.body.classList.remove("show-advanced");
}
$1763b227c722b862$var$update();
function $1763b227c722b862$var$input(id) {
    return document.getElementById(id);
}
function $1763b227c722b862$var$select(id) {
    return document.getElementById(id);
}
function $1763b227c722b862$export$df3937d3b537df0a() {
    return parseInt($1763b227c722b862$var$select('rotation').value) || 0;
}
function $1763b227c722b862$export$6c3e17132cc7c4b8() {
    return $1763b227c722b862$var$input("reflection").checked;
}
function $1763b227c722b862$export$5cdac61cf538712e() {
    return parseInt($1763b227c722b862$var$input('scale').value) || 3;
}
function $1763b227c722b862$export$c72d34660a162238() {
    return $1763b227c722b862$var$input('text').value;
}
function $1763b227c722b862$export$1fe8cbdc814f0f6f() {
    return $1763b227c722b862$var$select('ecc').value;
}
function $1763b227c722b862$export$c506eb22e615ba4a() {
    return parseInt($1763b227c722b862$var$input('version').value) || undefined;
}
function $1763b227c722b862$export$19e60219fa06d99f() {
    return parseInt($1763b227c722b862$var$select('mask').value) || undefined;
}
function $1763b227c722b862$export$56941163b29c5e38() {
    return !$1763b227c722b862$var$input("invert").checked;
}
function $1763b227c722b862$export$720ff43c24b7588c() {
    return $1763b227c722b862$var$input("diffuse-data").checked;
}
function $1763b227c722b862$export$87276d9d8d87b275() {
    return $1763b227c722b862$var$input("diffuse-free").checked;
}
function $1763b227c722b862$export$cfee15430f3abd6a() {
    return $1763b227c722b862$var$input("include-image").checked;
}
function $1763b227c722b862$export$87fa68f19e8ca532() {
    return $1763b227c722b862$var$input("hide-free").checked;
}
function $1763b227c722b862$export$e64c0035a6412445() {
    return $1763b227c722b862$var$input("lock-positioning").checked;
}
function $1763b227c722b862$export$2b3f7071b77aab84() {
    return $1763b227c722b862$var$input("lock-timing").checked;
}
function $1763b227c722b862$export$de064ab4e2e43cfe() {
    return $1763b227c722b862$var$input("lock-alignment").checked;
}
function $1763b227c722b862$export$9f82863358310abd() {
    return parseFloat($1763b227c722b862$var$input("gamma").value) || 2.2;
}
function $1763b227c722b862$export$850ce485a60d103b() {
    return parseFloat($1763b227c722b862$var$input("contrast").value) || 1.0;
}
function $1763b227c722b862$export$33cacb20a48e02bf() {
    return parseFloat($1763b227c722b862$var$input("brightness").value) || 0.0;
}
function $1763b227c722b862$export$fc5c648561bd3ad9() {
    return parseFloat($1763b227c722b862$var$input("min").value) || 0.0;
}
function $1763b227c722b862$export$af0a3f17eafc4426() {
    return parseFloat($1763b227c722b862$var$input("max").value) || 1.0;
}
function $1763b227c722b862$export$77508e4532dd50bc() {
    return parseFloat($1763b227c722b862$var$input("fudge").value) || 0.0;
}


function $d762c5704808b61d$export$2e2bcd8739ae039() {
    const text = (0, $1763b227c722b862$export$c72d34660a162238)();
    const options = {
        border: 0,
        ecc: (0, $1763b227c722b862$export$1fe8cbdc814f0f6f)(),
        version: (0, $1763b227c722b862$export$c506eb22e615ba4a)(),
        mask: (0, $1763b227c722b862$export$19e60219fa06d99f)(),
        scale: (0, $1763b227c722b862$export$5cdac61cf538712e)()
    };
    let qr;
    try {
        try {
            qr = (0, $631ab463e0c75d0e$export$2e2bcd8739ae039)(text, 'raw', options);
        } catch (e) {
            if (e.message == "Capacity overflow" && options.version < 40) qr = (0, $631ab463e0c75d0e$export$2e2bcd8739ae039)(text, 'raw', {
                ...options,
                version: undefined
            });
            else throw e;
        }
    } catch (e) {
        (0, $b5b4c813372c82a3$export$2e2bcd8739ae039)(e);
        throw e;
    }
    const l = qr.length;
    if ((0, $1763b227c722b862$export$56941163b29c5e38)()) qr = qr.map((row)=>row.map((c)=>!c));
    const rotation = (0, $1763b227c722b862$export$df3937d3b537df0a)();
    for(let i = 0; i < rotation; ++i){
        const newQr = [];
        for(let x = 0; x < l; ++x){
            newQr.push([]);
            for(let y = l - 1; y >= 0; --y)newQr[x][l - y - 1] = qr[y][x];
        }
        qr = newQr;
    }
    if ((0, $1763b227c722b862$export$6c3e17132cc7c4b8)()) for(let y = l - 1; y >= 0; --y)qr[y] = qr[y].reverse();
    return qr;
}




function $9dbaa47b608d6ce7$export$2e2bcd8739ae039(l, x, y) {
    l /= (0, $1763b227c722b862$export$5cdac61cf538712e)();
    x = ~~(x / (0, $1763b227c722b862$export$5cdac61cf538712e)());
    y = ~~(y / (0, $1763b227c722b862$export$5cdac61cf538712e)());
    // ob
    if (x < 0 || y < 0 || x >= l || y >= l) return true;
    // reflect
    if ((0, $1763b227c722b862$export$6c3e17132cc7c4b8)()) x = l - x - 1;
    // rotate
    const rotation = (0, $1763b227c722b862$export$df3937d3b537df0a)();
    for(let i = 0; i < rotation; ++i)[x, y] = [
        y,
        l - x - 1
    ];
    // corner blocks
    if (x < 7 && y < 7) return (0, $1763b227c722b862$export$e64c0035a6412445)();
    if (x < 7 && y > l - 8) return (0, $1763b227c722b862$export$e64c0035a6412445)();
    if (x > l - 8 && y < 7) return (0, $1763b227c722b862$export$e64c0035a6412445)();
    // timing lines
    if (x == 6 || y == 6) return (0, $1763b227c722b862$export$2b3f7071b77aab84)();
    // do we have extra blocks
    const blocks = $9dbaa47b608d6ce7$var$getBlocks(l);
    if (!blocks.length) return false;
    // are we too near a corner block to draw them
    const xBlock = $9dbaa47b608d6ce7$var$inBlock(x, blocks);
    const yBlock = $9dbaa47b608d6ce7$var$inBlock(y, blocks);
    // are we in one
    if (xBlock == null || yBlock == null) return false;
    // is it one we care about
    if (xBlock == 0 && yBlock == 0) return false;
    if (xBlock == 0 && yBlock == blocks.length - 1) return false;
    if (xBlock == blocks.length - 1 && yBlock == 0) return false;
    return (0, $1763b227c722b862$export$de064ab4e2e43cfe)();
}
function $9dbaa47b608d6ce7$var$inBlock(u, blocks) {
    for(let i = 0; i < blocks.length; ++i){
        const v = blocks[i];
        if (u < v - 2) return null;
        if (u < v + 3) return i;
    }
    return null;
}
function $9dbaa47b608d6ce7$export$5c152495ed2c5c39(x, y) {
    const scale = (0, $1763b227c722b862$export$5cdac61cf538712e)();
    const m = ~~(scale / 2);
    const xs = x % scale, ys = y % scale;
    if (xs == m && ys == m) return true;
    if (scale & 1) return false;
    if (xs == m && ys == m - 1) return true;
    if (xs == m - 1 && ys == m) return true;
    if (xs == m - 1 && ys == m - 1) return true;
    return false;
}
function $9dbaa47b608d6ce7$var$getBlocks(l) {
    const version = $9dbaa47b608d6ce7$var$qrVersion(l);
    return $9dbaa47b608d6ce7$var$dotPositions[version];
}
function $9dbaa47b608d6ce7$var$qrVersion(l) {
    return (l - 17) / 4;
}
function $9dbaa47b608d6ce7$var$qrSize(version) {
    return version * 4 + 17;
}
const $9dbaa47b608d6ce7$var$dotPositions = [
    /*0*/ null,
    /*1*/ [],
    /*2*/ [
        6,
        18
    ],
    /*3*/ [
        6,
        22
    ],
    /*4*/ [
        6,
        26
    ],
    /*5*/ [
        6,
        30
    ],
    /*6*/ [
        6,
        34
    ],
    /*7*/ [
        6,
        22,
        38
    ],
    /*8*/ [
        6,
        24,
        42
    ],
    /*9*/ [
        6,
        26,
        46
    ],
    /*10*/ [
        6,
        28,
        50
    ],
    /*11*/ [
        6,
        30,
        54
    ],
    /*12*/ [
        6,
        32,
        58
    ],
    /*13*/ [
        6,
        34,
        62
    ],
    /*14*/ [
        6,
        26,
        46,
        66
    ],
    /*15*/ [
        6,
        26,
        48,
        70
    ],
    /*16*/ [
        6,
        26,
        50,
        74
    ],
    /*17*/ [
        6,
        30,
        54,
        78
    ],
    /*18*/ [
        6,
        30,
        56,
        82
    ],
    /*19*/ [
        6,
        30,
        58,
        86
    ],
    /*20*/ [
        6,
        34,
        62,
        90
    ],
    /*21*/ [
        6,
        28,
        50,
        72,
        94
    ],
    /*22*/ [
        6,
        26,
        50,
        74,
        98
    ],
    /*23*/ [
        6,
        30,
        54,
        78,
        102
    ],
    /*24*/ [
        6,
        28,
        54,
        80,
        106
    ],
    /*25*/ [
        6,
        32,
        58,
        84,
        110
    ],
    /*26*/ [
        6,
        30,
        58,
        86,
        114
    ],
    /*27*/ [
        6,
        34,
        62,
        90,
        118
    ],
    /*28*/ [
        6,
        26,
        50,
        74,
        98,
        122
    ],
    /*29*/ [
        6,
        30,
        54,
        78,
        102,
        126
    ],
    /*30*/ [
        6,
        26,
        52,
        78,
        104,
        130
    ],
    /*31*/ [
        6,
        30,
        56,
        82,
        108,
        134
    ],
    /*32*/ [
        6,
        34,
        60,
        86,
        112,
        138
    ],
    /*33*/ [
        6,
        30,
        58,
        86,
        114,
        142
    ],
    /*34*/ [
        6,
        34,
        62,
        90,
        118,
        146
    ],
    /*35*/ [
        6,
        30,
        54,
        78,
        102,
        126,
        150
    ],
    /*36*/ [
        6,
        24,
        50,
        76,
        102,
        128,
        154
    ],
    /*37*/ [
        6,
        28,
        54,
        80,
        106,
        132,
        158
    ],
    /*38*/ [
        6,
        32,
        58,
        84,
        110,
        136,
        162
    ],
    /*39*/ [
        6,
        26,
        54,
        82,
        110,
        138,
        166
    ],
    /*40*/ [
        6,
        30,
        58,
        86,
        114,
        142,
        170
    ]
];


const $ef4c3eb9f5926687$var$canvas = document.getElementById("output");
function $ef4c3eb9f5926687$export$2e2bcd8739ae039(data) {
    const l = data.length;
    $ef4c3eb9f5926687$var$canvas.width = l;
    $ef4c3eb9f5926687$var$canvas.height = l;
    const ctx = $ef4c3eb9f5926687$var$canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, l, l);
    let i = 0;
    const hideFreePoints = (0, $1763b227c722b862$export$87fa68f19e8ca532)();
    for(let y = 0; y < l; ++y)for(let x = 0; x < l; ++x){
        if (hideFreePoints && !(0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(l, x, y) && !(0, $9dbaa47b608d6ce7$export$5c152495ed2c5c39)(x, y)) {
            i += 3;
            imgData.data[i++] = 0;
            continue;
        }
        for(let c = 0; c < 3; ++c)imgData.data[i++] = +data[y][x] * 255;
        imgData.data[i++] = 255;
    }
    ctx.putImageData(imgData, 0, 0);
}




const $0e22864b1e658c82$var$input = document.getElementById('upload');
function $0e22864b1e658c82$export$2e2bcd8739ae039(l) {
    if ($0e22864b1e658c82$var$input.files.length != 1) throw new Error("Upload a file");
    return new Promise((resolve, reject)=>{
        const img = new Image();
        img.addEventListener("load", (e)=>{
            const canvas = document.createElement("canvas");
            canvas.width = l;
            canvas.height = l;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, l, l);
            const imgData = ctx.getImageData(0, 0, l, l);
            const output = [];
            const gamma = (0, $1763b227c722b862$export$9f82863358310abd)();
            const contrast = (0, $1763b227c722b862$export$850ce485a60d103b)();
            const brightness = (0, $1763b227c722b862$export$33cacb20a48e02bf)();
            const minBrightness = (0, $1763b227c722b862$export$fc5c648561bd3ad9)();
            const maxBrightness = (0, $1763b227c722b862$export$af0a3f17eafc4426)();
            for(let y = 0; y < l; ++y){
                const row = [];
                output.push(row);
                for(let x = 0; x < l; ++x){
                    let value = imgData.data[(x + y * l) * 4 + 1] / 255;
                    value **= gamma;
                    value -= 0.5;
                    value *= contrast;
                    value += brightness + 0.5;
                    if (value < minBrightness) value = minBrightness;
                    if (value > maxBrightness) value = maxBrightness;
                    row.push(value);
                }
            }
            resolve(output);
        });
        const reader = new FileReader();
        reader.addEventListener('load', (e)=>img.src = e.target.result);
        reader.readAsDataURL($0e22864b1e658c82$var$input.files[0]);
    });
}




function $39e6cfb69a8e0a77$export$d3752ca699d71b13(imageData, qr) {
    const scale = (0, $1763b227c722b862$export$5cdac61cf538712e)();
    if (scale & 1) {
        for(let y = 0; y < imageData.length; ++y)for(let x = 0; x < imageData[y].length; ++x){
            if ((0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(imageData.length, x, y)) continue;
            if (!(0, $9dbaa47b608d6ce7$export$5c152495ed2c5c39)(x, y)) continue;
            const error = imageData[y][x] - +qr[y][x];
            imageData[y - 1][x] += error * 3 / 16;
            imageData[y + 1][x] += error * 3 / 16;
            imageData[y][x - 1] += error * 3 / 16;
            imageData[y][x + 1] += error * 3 / 16;
            imageData[y - 1][x - 1] += error / 16;
            imageData[y + 1][x - 1] += error / 16;
            imageData[y - 1][x + 1] += error / 16;
            imageData[y + 1][x + 1] += error / 16;
        }
        return;
    }
    const m = scale / 2 - 1;
    for(let y = m; y < imageData.length; y += scale)for(let x = m; x < imageData.length; x += scale){
        if ((0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(imageData.length, x, y)) continue;
        for (const dx of [
            -1,
            1
        ])for (const dy of [
            -1,
            1
        ]){
            const sx = x + 0.5 + dx * 0.5;
            const sy = y + 0.5 + dy * 0.5;
            const error = imageData[sy][sx] - +qr[sy][sx];
            imageData[sy][sx + dx] += error * 6 / 16;
            imageData[sy + dy][sx] += error * 6 / 16;
            imageData[sy + dy][sx + dx] += error * 4 / 16;
        }
    }
}
function $39e6cfb69a8e0a77$export$3b9c8b3c8eceb178(imageData) {
    for(let y = 0; y < imageData.length; ++y)for(let x = 0; x < imageData[y].length; ++x){
        if (!canChange(x, y)) continue;
        const actual = Math.round(imageData[y][x]);
        const error = imageData[y][x] - actual;
        imageData[y][x] = actual;
        const a = canChange(x + 1, y);
        const b = canChange(x - 1, y + 1);
        const c = canChange(x, y + 1);
        const d = canChange(x + 1, y + 1);
        const total = +a * 7 + +b * 3 + +c * 5 + +d;
        if (a) imageData[y][x + 1] += error * 7 / total;
        if (b) imageData[y + 1][x - 1] += error * 3 / total;
        if (c) imageData[y + 1][x] += error * 5 / total;
        if (d) imageData[y + 1][x + 1] += error / total;
    }
    function canChange(x, y) {
        return !(0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(imageData.length, x, y) && !(0, $9dbaa47b608d6ce7$export$5c152495ed2c5c39)(x, y);
    }
}




function $70d009aef91e8d3e$export$2e2bcd8739ae039(imageData, qr) {
    const f = (0, $1763b227c722b862$export$77508e4532dd50bc)();
    if (!f) return;
    const l = qr.length;
    const values = [];
    for(let y = 0; y < l; ++y)for(let x = 0; x < l; ++x)if (!(0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(l, y, x) && (0, $9dbaa47b608d6ce7$export$5c152495ed2c5c39)(y, x)) values.push({
        x: x,
        y: y,
        error: Math.abs(imageData[y][x] - +qr[y][x])
    });
    values.sort((a, b)=>b.error - a.error);
    for(let i = 0; i < f * values.length; ++i){
        const { x: x, y: y } = values[i];
        qr[y][x] = imageData[y][x] > 0.5;
    }
}



const $b16a1a1d03ec89fa$var$canvas = document.getElementById("output");
async function $b16a1a1d03ec89fa$var$update() {
    (0, $b5b4c813372c82a3$export$a15ba4bdf59dacc9)();
    $b16a1a1d03ec89fa$var$canvas.classList.add("loading");
    if ((0, $1763b227c722b862$export$56941163b29c5e38)()) document.body.classList.remove("uninverted");
    else document.body.classList.add("uninverted");
    if ((0, $1763b227c722b862$export$87fa68f19e8ca532)()) document.body.classList.add("hide-free");
    else document.body.classList.remove("hide-free");
    try {
        const qr = (0, $d762c5704808b61d$export$2e2bcd8739ae039)();
        const imageData = await (0, $0e22864b1e658c82$export$2e2bcd8739ae039)(qr.length);
        (0, $70d009aef91e8d3e$export$2e2bcd8739ae039)(imageData, qr);
        if ((0, $1763b227c722b862$export$720ff43c24b7588c)()) (0, $39e6cfb69a8e0a77$export$d3752ca699d71b13)(imageData, qr);
        if ((0, $1763b227c722b862$export$87276d9d8d87b275)()) (0, $39e6cfb69a8e0a77$export$3b9c8b3c8eceb178)(imageData);
        if ((0, $1763b227c722b862$export$cfee15430f3abd6a)()) for(let x = 0; x < qr.length; ++x)for(let y = 0; y < qr.length; ++y){
            if ((0, $9dbaa47b608d6ce7$export$2e2bcd8739ae039)(qr.length, x, y)) continue;
            if ((0, $9dbaa47b608d6ce7$export$5c152495ed2c5c39)(x, y)) continue;
            qr[y][x] = imageData[y][x] > 0.5;
        }
        (0, $ef4c3eb9f5926687$export$2e2bcd8739ae039)(qr);
        await $b16a1a1d03ec89fa$var$expand();
        document.getElementById("download").href = $b16a1a1d03ec89fa$var$canvas.toDataURL('png');
        $b16a1a1d03ec89fa$var$canvas.classList.remove("hidden");
    // const ctx = canvas.getContext('2d');
    // const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // console.log(decodeQR(imgData));
    } catch (e) {
        $b16a1a1d03ec89fa$var$canvas.classList.add("hidden");
        (0, $b5b4c813372c82a3$export$2e2bcd8739ae039)(e);
    } finally{
        $b16a1a1d03ec89fa$var$canvas.classList.remove("loading");
    }
}
document.getElementById("form").addEventListener("input", $b16a1a1d03ec89fa$var$update);
$b16a1a1d03ec89fa$var$update();
async function $b16a1a1d03ec89fa$var$expand(amount = 25) {
    const img = await $b16a1a1d03ec89fa$var$canvasToImage($b16a1a1d03ec89fa$var$canvas);
    $b16a1a1d03ec89fa$var$canvas.width = img.width * amount + amount * 10;
    $b16a1a1d03ec89fa$var$canvas.height = img.height * amount + amount * 10;
    const context = $b16a1a1d03ec89fa$var$canvas.getContext("2d");
    context.fillStyle = (0, $1763b227c722b862$export$56941163b29c5e38)() ? 'white' : 'black';
    context.fillRect(0, 0, $b16a1a1d03ec89fa$var$canvas.width, $b16a1a1d03ec89fa$var$canvas.height);
    if ((0, $1763b227c722b862$export$87fa68f19e8ca532)()) context.clearRect(amount * 5, amount * 5, img.width * amount, img.height * amount);
    context.imageSmoothingEnabled = false;
    context.drawImage(img, amount * 5, amount * 5, img.width * amount, img.height * amount);
}
function $b16a1a1d03ec89fa$var$canvasToImage(canvas) {
    return new Promise((resolve, reject)=>{
        const img = new Image();
        img.addEventListener("load", ()=>resolve(img));
        img.addEventListener("error", reject);
        img.src = canvas.toDataURL("png");
    });
}


//# sourceMappingURL=dithered-qr-codes.a3cfa645.js.map
